import { Button } from "@/components/ui/button";
import { InnerPageActions } from "@/components/site/InnerPageActions";
import { FaqList } from "@/components/site/FaqList";
import { VehicleSpecifications } from "@/components/site/VehicleSpecifications";
import { RATING_VALUE, REVIEW_COUNT, GOOGLE_BUSINESS_URL } from "@/lib/schema";
import { winterTireAnswer } from "@/lib/vehicle-policy";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { motion, AnimatePresence } from "motion/react";
import { useState, useSyncExternalStore } from "react";
import { MobileVehiclePage } from "@/components/site/MobileVehiclePage";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { getCar, cars } from "@/lib/cars";
import { MobileRateCard } from "@/components/site/MobileRateCard";
import { imageDims } from "@/lib/image-dims";
import { galleryAlt } from "@/lib/gallery-alt";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import { ReviewsHighlight } from "@/components/site/ReviewsHighlight";
import {
  breadcrumbScript,
  faqScript,
  type FaqItem,
} from "@/lib/schema";

const carFaqs: Record<string, FaqItem[]> = {
  "range-rover-p530-lwb-2024": [
    {
      question: "What does LWB mean on the 2024 Range Rover P530?",
      answer: "LWB means long wheelbase. The longer body gives rear passengers more room to stretch out, making this Range Rover a strong choice for airport arrivals and longer family drives around Vancouver.",
    },
    {
      question: "What engine is in the Range Rover P530 LWB?",
      answer: "The P530 uses a 4.4L twin-turbo V8 rated at 523 horsepower, paired with all-wheel drive. It delivers smooth, effortless performance on city streets and the highway.",
    },
    {
      question: "How many people can this Range Rover seat?",
      answer: "This 2024 Range Rover P530 LWB seats seven and has a black interior.",
    },
    {
      question: "How much does the Range Rover P530 LWB cost to rent?",
      answer: "Like the Cadillac Escalade, the Range Rover is $395/day for 24 hours and $295/day for 48 hours. Three to six days are $275/day, and seven days or longer are $250/day. It includes 150 km/day; extra kilometres are $1.75/km, the security deposit is $2,000, and LDW is $60/day.",
    },
    {
      question: "Can you deliver the Range Rover to YVR or my hotel?",
      answer: "Yes. Ask us about delivering the Range Rover to YVR, your hotel, or your residence in Metro Vancouver. Delivery outside Vancouver and longer trips, including Whistler, are arranged and quoted when you book.",
    },
  ],
  "cadillac-escalade-2023": [
    {
      question: "How many people can the 2023 Cadillac Escalade seat?",
      answer:
        "The Escalade seats seven adults across three rows. It's the largest vehicle in our Vancouver fleet, which is why it's a natural pick for wedding parties, touring artists, athletes, and executive airport transfers where you need real space for passengers plus their luggage in a single vehicle.",
    },
    {
      question: "Is the Escalade a good YVR airport pickup with luggage?",
      answer:
        "Yes. With seven seats and a full-size cargo area behind the third row, the Escalade easily handles a full airline complement of checked bags plus carry-ons. Delivery to YVR is complimentary inside our free-delivery zone, and we can meet arrivals curbside at International Level 2 or Domestic Door 8.",
    },
    {
      question: "What engine does the 2023 Escalade have?",
      answer:
        "This Escalade runs the 3.0L turbo-diesel six, paired with full-time 4WD. It delivers the effortless low-end torque you expect from a full-size diesel SUV: confident on the Sea-to-Sky, quiet on the highway, and unfazed by winter conditions between Vancouver and Whistler.",
    },
    {
      question: "Can you deliver the Escalade to Whistler?",
      answer:
        "Yes. We regularly deliver the Escalade up the Sea-to-Sky to Whistler, with the delivery fee quoted at booking. Free delivery is included inside Vancouver city limits; anywhere further (Squamish, Whistler, Abbotsford, or the Okanagan) we arrange on request. See our delivery page for full details.",
    },
    {
      question: "How much is an Escalade rental in Vancouver?",
      answer:
        "Escalade rates are $395/day for 24 hours, $295/day for 48 hours, $250/day for 3-6 days, and $225/day for 7+ days. Rates are CAD before taxes and mandatory $60/day LDW. The refundable deposit is $2,000; 150 km/day are included, with extra kilometres at $1.75/km.",
    },
  ],
  "bmw-x7-m60i-2024": [
    {
      question: "How many people can the BMW X7 M60i seat?",
      answer:
        "This X7 M60i is our six-seat configuration, with second-row captain chairs and a third row. It's a genuine three-row layout with dedicated aisle access to the back, which is why it's popular for Whistler ski weeks and executive airport transfers where every passenger wants their own seat.",
    },
    {
      question: "How much power does the X7 M60i's V8 make?",
      answer:
        "The M60i uses BMW's 4.4L twin-turbo V8 producing 523 horsepower, driving all four wheels through xDrive AWD. It's genuinely quick for a three-row SUV, pulls confidently up the Sea-to-Sky without ever feeling strained, and stays hushed cruising through Kitsilano and Yaletown.",
    },
    {
      question: "Can the X7 handle a YVR pickup for a family with luggage?",
      answer:
        "Yes. With six captain seats and generous cargo space behind the third row, the X7 comfortably takes a family with checked bags and carry-ons. Free YVR delivery is included inside our Vancouver zone, and we can meet arrivals curbside at International Level 2 or Domestic Door 8.",
    },
    {
      question: "Is the X7 M60i suitable for a Whistler ski week?",
      answer: winterTireAnswer("bmw-x7-m60i-2024"),
    },
    {
      question: "What does the X7 cost for a weekend rental?",
      answer:
        "The X7 M60i is $295 for the first 24 hours and $275/day for a 48-hour weekend, dropping to $250/day at 3-6 days and $225/day at seven-plus. Security deposit is $2,000, LDW is $60/day, and 150 km/day are included with $1.75 per extra kilometre.",
    },
  ],
  "mercedes-s-class-2022": [
    {
      question: "How many people fit in the Mercedes S580?",
      answer:
        "The S580 seats five, but it's genuinely built around the rear two passengers. Expect executive-package reclining rear seats, dedicated rear climate control, and enough legroom for adults on long transfers. It's our first pick for chauffeur-style airport pickups and downtown VIP transport.",
    },
    {
      question: "Is the S580 a good wedding day car?",
      answer:
        "Yes, it's our most-requested wedding sedan. The beautiful black paint, hushed rear cabin, and rear-axle steering (which makes tight venue driveways easy) all suit the day. We hold the car for the full booking window and deliver directly to your hotel or venue at the agreed time.",
    },
    {
      question: "What engine does the Mercedes S580 have?",
      answer:
        "The S580 runs a 4.0L biturbo V8 producing 496 horsepower with 48-volt mild-hybrid assist, driving all four wheels through 4MATIC AWD. It's smooth rather than aggressive: the drivetrain is tuned for effortless overtakes on the Sea-to-Sky and near-silent cruising through downtown Vancouver.",
    },
    {
      question: "Do you deliver the S580 to YVR?",
      answer:
        "Yes. Free delivery is included inside Vancouver city limits, and YVR is a short hop we handle daily. We can meet arrivals curbside at International Level 2 or Domestic Door 8, or drop the car at your downtown, Coal Harbour, or Yaletown hotel ready to go.",
    },
    {
      question: "How much is a Mercedes S-Class rental in Vancouver?",
      answer:
        "S580 rates are $395/day for 24 hours, $295/day for 48 hours, $250/day for 3-6 days, and $225/day for 7+ days. Rates are CAD before taxes and mandatory $60/day LDW. The refundable deposit is $2,000; 150 km/day are included, with extra kilometres at $1.75/km.",
    },
  ],
  "porsche-macan-2023": [
    {
      question: "Is the Macan a real Porsche driving experience?",
      answer:
        "Yes. The Macan shares Porsche's steering feel, brake response, and chassis balance. It just happens to sit taller. Sharp turn-in on the Stanley Park causeway, confident AWD through West Coast rain, and a compact footprint that slips into any downtown parkade or Kits side street.",
    },
    {
      question: "How many people and how much luggage fit in the Macan?",
      answer:
        "Five seats and a proper SUV cargo area behind the second row, comfortably a couple's weekend of luggage, a pair of ski bags, or golf clubs for two. Fold the rear seats and it swallows larger loads for Okanagan wine trips or Tofino getaways.",
    },
    {
      question: "Is the Macan good for Whistler in winter?",
      answer: winterTireAnswer("porsche-macan-2023"),
    },
    {
      question: "What's the daily rate and mileage on the Macan?",
      answer:
        "The Macan is $295 for the first 24 hours, $230/day at 48 hours, $180/day for 3-6 days, and $150/day at seven-plus. Every day includes 150 km, with extra kilometres at $1.50 each. Security deposit is $2,000 and LDW is $60/day.",
    },
    {
      question: "Can you deliver the Macan to my hotel?",
      answer:
        "Yes, delivery inside Vancouver city limits is complimentary, including hotels in Coal Harbour, downtown, Yaletown, the West End, and Kitsilano. YVR meets and drop-offs are included in that same free-delivery zone. For Richmond, Burnaby, the North Shore, Whistler, and beyond we quote a delivery fee at booking.",
    },
  ],
  "bmw-m3-2023": [
    {
      question: "Is the M3 Competition rear-wheel drive or all-wheel drive?",
      answer:
        "This M3 Competition is rear-wheel drive, the classic M3 layout. It's fantastic on dry summer roads and Sea-to-Sky day trips, but for winter Whistler runs, contact us about the Escalade, the only vehicle currently fitted with winter tires. RWD is a big part of what makes it feel alive.",
    },
    {
      question: "How fast is the M3 Competition to 60 mph?",
      answer:
        "The M3 Competition runs 0-60 mph in 3.8 seconds, produced by BMW's 3.0L twin-turbo inline-six making 503 horsepower. It's genuinely quick, and the exhaust note through the Stanley Park tunnels or climbing the Sea-to-Sky is worth the rental on its own.",
    },
    {
      question: "Can I take the M3 to the track?",
      answer:
        "Track use is not covered by our insurance: the LDW and comprehensive coverage exclude on-track incidents. If you're planning a day at Vancouver Island Motorsport Circuit or Mission Raceway, get in touch first and we'll walk you through the specific arrangements before you book.",
    },
    {
      question: "How much luggage fits in the M3?",
      answer:
        "The M3 is a four-door sedan with a real trunk, so expect a couple's weekend of luggage or a pair of golf bags comfortably. It seats five on paper; four adults ride best. It's an enthusiast's driver's car first, so if you need cargo room prioritise the X1 or Macan.",
    },
    {
      question: "What's the rate and security deposit on the M3 Competition?",
      answer:
        "The M3 is $295 for 24 hours, $275/day at 48 hours, $250/day for 3-6 days, and $225/day at seven-plus. Security deposit is $2,000, LDW is $60/day, and every day includes 150 km with $1.50 per extra kilometre.",
    },
  ],
  "bmw-m440-2021": [
    {
      question: "Is the M440i as fast as the M3?",
      answer:
        "Not quite. The M440i does 0-60 mph in 4.3 seconds versus the M3's 3.8. Its 3.0L turbo inline-six mild-hybrid makes 382 hp through xDrive AWD, so it's the better all-weather choice, easier to daily on Vancouver's imperfect asphalt, and still genuinely quick when you want it.",
    },
    {
      question: "Does the M440i have all-wheel drive?",
      answer:
        "Yes, this M440i is the xDrive AWD variant, which is why it's popular for shoulder-season Whistler runs, wet West Vancouver commutes, and Sea-to-Sky day trips when the weather can turn quickly. xDrive makes 382 horsepower usable in conditions where a rear-driver would struggle.",
    },
    {
      question: "Can four adults ride comfortably in the M440i?",
      answer:
        "The M440i is a two-door coupe with usable back seats. Two up front are perfectly comfortable, and shorter trips with four adults are fine, but for longer drives or heavy luggage we'd point you at the M3 sedan (also $295/day) or the X1 instead.",
    },
    {
      question: "How much does the M440i cost for a weekend?",
      answer:
        "The M440i is $295 for the first 24 hours and $250/day at 48 hours, dropping to $200/day for 3-6 days and $150/day at seven-plus. Security deposit is $2,000, LDW is $60/day, and 150 km/day are included with $1.50 per extra kilometre.",
    },
    {
      question: "Can you deliver the M440i to Whistler or the Sea-to-Sky?",
      answer: winterTireAnswer("bmw-m440-2021"),
    },
  ],
  "bmw-x1-2023": [
    {
      question: "How much does the BMW X1 cost per day?",
      answer:
        "The X1 is our most-accessible vehicle at $200 for the first 24 hours, $150/day at 48 hours, $100/day for 3-6 days, and $75/day at seven-plus. Security deposit is $2,000, LDW is $60/day, and every day includes 150 km with $1.50 per extra kilometre.",
    },
    {
      question: "Is the X1 a good YVR airport rental?",
      answer:
        "Yes, it's one of our most-booked airport pickups. Real trunk space for two travellers with checked bags, xDrive AWD for wet-weather confidence, and a compact footprint that's easy in downtown parkades. Free YVR delivery is included inside our Vancouver zone.",
    },
    {
      question: "Does the X1 have all-wheel drive?",
      answer:
        "Yes, this X1 is the xLine xDrive AWD variant, so it handles West Coast rain, shoulder-season Sea-to-Sky drives, and light Whistler snow without drama. The 2.0L turbo four produces 241 hp, which is plenty for city merges and highway passes without ever feeling underpowered.",
    },
    {
      question: "How many people and how much luggage fit in the X1?",
      answer:
        "Five seats and a real SUV cargo area behind the rear bench. Comfortably a couple's week of luggage or four passengers with weekender bags. Fold the second row down for skis, larger suitcases, or a Costco run. It's genuinely practical for its compact footprint.",
    },
    {
      question: "Can the X1 handle a Whistler weekend?",
      answer: winterTireAnswer("bmw-x1-2023"),
    },
  ],
};

const carCopy: Record<string, { drive: string; renters: string; included: string }> = {
  "range-rover-p530-lwb-2024": {
    drive: "On the way from YVR into Vancouver, the P530 has the calm, unhurried feel people expect from a Range Rover. Its long-wheelbase proportions give the cabin a sense of space, while the twin-turbo V8 provides plenty of power for highway merging and the drive along the Sea-to-Sky. All-wheel drive adds reassurance when the weather turns wet.",
    renters: "Families planning a comfortable getaway, visitors arriving with luggage, and clients who want a refined seven-seat SUV with a black interior for a wedding or business trip.",
    included: "150 km per day, a $2,000 refundable security deposit, and LDW at $60 per day. Delivery can be arranged at your address or YVR; ask for a quote outside the complimentary Vancouver delivery area.",
  },
  "cadillac-escalade-2023": {
    drive: "On Vancouver roads the Escalade feels commanding without feeling clumsy. Air suspension smooths out the Cambie Bridge expansion joints and Sea-to-Sky frost heaves alike, the 3.0L turbo-diesel pulls confidently up the Cypress and Grouse climbs, and full-time 4WD keeps you planted through winter rain from Richmond to Whistler. Adaptive cruise, Super Cruise-ready driver aids, and a panoramic view camera make downtown parkades and narrow West End streets far less stressful than you'd expect for a full-size SUV.",
    renters: "Executives arriving at YVR with a full team, wedding parties needing a statement airport-to-venue transfer, athletes and touring artists, and families running Whistler ski weekends. The seven-seat cabin with second-row captain seating swallows luggage, ski bags, and golf clubs without compromise.",
    included: "150 km per day, complimentary delivery inside Vancouver, unbranded vehicles, comprehensive insurance to $5M liability, and 24/7 roadside support.",
  },
  "bmw-x7-m60i-2024": {
    drive: "The X7 M60i's twin-turbo V8 turns the drive up the Sea-to-Sky into a genuine event, but the same car settles into a hushed cruiser through Kitsilano and Yaletown. xDrive AWD, adaptive air suspension, and integral active steering shrink a very big SUV in tight downtown parkades and on the switchbacks above Squamish. Bowers & Wilkins audio, massaging seats, and glass panoramic roof make even a rainy 45-minute crawl through the George Massey Tunnel feel civilised.",
    renters: "Families and business travellers who want three real rows without giving up sports-car pace. Popular for Whistler ski weeks with six adults, executive airport transfers from YVR, and multi-day road trips out to Tofino or Kelowna.",
    included: "150 km per day, free delivery in Vancouver, unbranded vehicles, full comprehensive coverage with $5M third-party liability, and 24/7 roadside assistance.",
  },
  "mercedes-s-class-2022": {
    drive: "The S580 is engineered to make Vancouver's worst commute feel like a first-class cabin. Rear-axle steering makes U-turns on Robson trivial, air suspension floats over potholes on Main Street, and 4MATIC AWD keeps 496 hp planted through wet coastal weather. Rear passengers get executive-package reclining seats, dedicated climate, and a hushed cabin that turns the drive from YVR to a downtown hotel into productive time rather than lost time.",
    renters: "Corporate travellers, wedding couples, film-industry clients, and anyone hosting VIPs who want the definitive luxury sedan for airport pickups, downtown meetings, or a Stanley Park photo drive. Frequently booked with a chauffeur-style delivery to hotels in Coal Harbour and downtown.",
    included: "150 km per day, complimentary delivery within Vancouver, unbranded vehicles, full comprehensive insurance with $5M liability coverage, and 24/7 roadside assistance.",
  },
  "porsche-macan-2023": {
    drive: "The Macan drives like a hot hatch that happens to have SUV ground clearance. Sharp turn-in on the Stanley Park causeway, responsive AWD on Vancouver roads, and a compact footprint that slots into any downtown parkade or Kits side street. The turbo four is punchy enough for the Sea-to-Sky and quiet enough for a downtown crawl, and the seating position gives you the visibility of an SUV without the bulk.",
    renters: "Couples and small families who want a genuine Porsche experience without going full 911. A favourite for Whistler weekends, Okanagan wine trips, and clients who want a sportier alternative to the usual German sedan while keeping room for luggage and skis.",
    included: "150 km per day, free delivery in Vancouver, unbranded vehicles, full comprehensive insurance with $5M third-party liability, and 24/7 roadside support.",
  },
  "bmw-m3-2023": {
    drive: "The M3 Competition is the reason people learn to drive stick again, even though this one's an automatic. On the Sea-to-Sky it's ferocious, on the Duffey Lake loop it's addictive, and around town it's tractable enough to daily. RWD means you feel every input; the adaptive M suspension keeps things composed on Vancouver's patchy pavement, and the exhaust note through the Stanley Park tunnels is worth the rental on its own.",
    renters: "Enthusiast drivers on birthday weekends, photographers and content creators, and clients who want a real driver's car for a Sea-to-Sky day trip. Also a common pick for track days at Vancouver Island Motorsport Circuit and Mission Raceway.",
    included: "150 km per day, complimentary delivery inside Vancouver, unbranded vehicles, comprehensive insurance with $5M liability, and 24/7 roadside support. Track use is not covered. Ask us about track-day arrangements.",
  },
  "bmw-m440-2021": {
    drive: "The M440i is the coupe that makes every drive feel a little more special. The B58 inline-six pulls smoothly from a stoplight on Burrard to triple-digit speeds on the Sea-to-Sky, xDrive keeps it planted through West Vancouver rain, and the ride is composed enough to daily on Vancouver's imperfect asphalt. Drop the top on a clear afternoon at English Bay and it's hard to think of a better way to see the city.",
    renters: "Couples on getaway weekends, wedding grooms, birthday and anniversary bookings, and visitors who want a stylish two-door for a Stanley Park cruise, a Whistler run, or a coastal drive to Horseshoe Bay.",
    included: "150 km per day, complimentary delivery in Vancouver, unbranded vehicles, full comprehensive insurance with $5M liability, and 24/7 roadside assistance.",
  },
  "bmw-x1-2023": {
    drive: "The X1 xLine is the easiest way to spend a weekend in a proper BMW. The 2.0L turbo has real shove for city merges and Sea-to-Sky passes, xDrive gives you all-weather confidence from Richmond to Whistler, and the compact footprint slips into any downtown parkade or Gastown side street. Modern curved-display iCockpit, wireless CarPlay, and a hushed cabin make it a genuinely relaxing daily driver.",
    renters: "Solo travellers, couples, and small families who want a premium SUV without the size or price of a full-size. Popular for YVR pickups, week-long visits to Vancouver, and clients trading up from a rental-counter economy car.",
    included: "150 km per day, complimentary delivery inside Vancouver, unbranded vehicles, full comprehensive insurance with $5M liability, and 24/7 roadside support.",
  },
};



export const Route = createFileRoute("/fleet/$slug")({
  component: CarDetail,
  loader: ({ params }) => {
    const car = getCar(params.slug);
    if (!car) throw notFound();
    return { car };
  },
  head: ({ loaderData, params }) => {
    const car = loaderData?.car;
    const title = car ? `${car.slug === "mercedes-s-class-2022" ? "Mercedes S-Class" : car.shortName} Rental Vancouver${car.slug === "mercedes-s-class-2022" ? " (S580)" : ""} | Avolus Cars` : "Fleet | Avolus Cars";
    const desc = car
      ? `Rent the ${car.shortName} in Vancouver from $${car.pricePerDay}/day. Free delivery in Vancouver and to YVR. Book online with Avolus Cars.`
      : "Avolus luxury car rental in Vancouver.";
    const url = `https://avoluscars.com/fleet/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: url },
        ...(car?.image.startsWith("https://") ? [{ property: "og:image", content: car.image }, { name: "twitter:image", content: car.image }] : []),
              ],
      links: [{ rel: "canonical", href: url }],
      scripts: car
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Car",
                name: car.name,
                alternateName: `${car.shortName} Rental Vancouver`,
                description: car.description,
                image: car.image,
                brand: {
                  "@type": "Brand",
                  name: car.name.split(" ")[1] ?? car.shortName.split(" ")[0],
                },
                model: car.shortName,
                vehicleModelDate: car.name.match(/\b(19|20)\d{2}\b/)?.[0],
                bodyType: car.category,
                numberOfDoors: car.category.toLowerCase().includes("coupe") ? 2 : 4,
                vehicleSeatingCapacity:
                  Number(car.specs.find((s) => s.label === "Seats")?.value) || undefined,
                vehicleEngine: {
                  "@type": "EngineSpecification",
                  name: car.specs.find((s) => s.label === "Engine")?.value,
                },
                driveWheelConfiguration: car.specs.find((s) => s.label === "Drive")?.value,
                offers: {
                  "@type": "Offer",
                  price: String(car.pricePerDay),
                  priceCurrency: "CAD",
                  availability: "https://schema.org/InStock",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: car.pricePerDay,
                    priceCurrency: "CAD",
                    unitCode: "DAY",
                  },
                  seller: { "@id": "https://avoluscars.com/#business" },
                  areaServed: ["Vancouver", "Metro Vancouver", "YVR", "Whistler"],
                },
              }),
            },
            breadcrumbScript([
              { name: "Home", url: "https://avoluscars.com/" },
              { name: "Fleet", url: "https://avoluscars.com/fleet" },
              { name: car.shortName, url },
            ]),
            ...(carFaqs[car.slug] ? [faqScript(carFaqs[car.slug])] : []),
          ]
        : [],
    };
  },
});

function subscribeVehicleViewport(onChange: () => void) {
  const query = window.matchMedia("(max-width: 767px)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function CarDetail() {
  const { car } = Route.useLoaderData();
  const mobile = useSyncExternalStore(subscribeVehicleViewport, () => window.matchMedia("(max-width: 767px)").matches, () => null);
  return <>
    {mobile !== false && <div className="md:hidden"><MobileVehiclePage car={car} /></div>}
    {mobile !== true && <div className="hidden md:block"><DesktopCarDetail /></div>}
  </>;
}

function DesktopCarDetail() {
  const { car } = Route.useLoaderData();
  const others = cars.filter((c) => c.slug !== car.slug).slice(0, 3);

  const gallery = car.images && car.images.length > 0 ? car.images : [car.image];
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const activeImage = gallery[activeIndex];
  const activeAlt = galleryAlt(car.name, activeImage, activeIndex);
  const goPrev = () => setActiveIndex((i) => (i - 1 + gallery.length) % gallery.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % gallery.length);


  return (
    <Layout inner>
      <section className="pt-32 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Link
            to="/fleet"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back to fleet
          </Link>

          <div className="vehicle-top grid gap-12 lg:grid-cols-5 items-start">
            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="vehicle-gallery lg:col-span-3"
            >
              <div className="md:hidden relative">
                <div className="flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none]" aria-label={`${car.shortName} photo gallery`}
                  onScroll={(event) => { const el = event.currentTarget; if (el.clientWidth) setActiveIndex(Math.round(el.scrollLeft / el.clientWidth)); }}>
                  {gallery.map((src, i) => <div key={src + i} className="w-full shrink-0 snap-start aspect-[4/3]">
                    <img src={src} alt={galleryAlt(car.name, src, i)} width={imageDims(src).width} height={imageDims(src).height} loading={i === 0 ? "eager" : "lazy"} className="w-full h-full object-cover" />
                  </div>)}
                </div>
                <span className="absolute bottom-3 right-3 bg-foreground text-background px-3 py-1 text-xs">{`${activeIndex + 1} / ${gallery.length}`}</span>
              </div>
              <div className="hidden md:block relative bg-card border border-border overflow-hidden aspect-[3/2] group">
                <AnimatePresence initial={false} mode="wait">
                  <motion.img
                    key={activeImage}
                    src={activeImage}
                    alt={activeAlt}
                    width={imageDims(activeImage).width}
                    height={imageDims(activeImage).height}
                    fetchPriority={activeIndex === 0 ? "high" : "auto"}
                    decoding="async"
                    initial={false}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    onClick={() => setLightboxOpen(true)}
                    className="w-full h-full object-cover border-0 cursor-zoom-in"
                  />
                </AnimatePresence>
                {gallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrev}
                      aria-label="Previous photo"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      aria-label="Next photo"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight size={20} />
                    </button>
                    <div className="absolute bottom-3 right-3 px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-black/60 text-white">
                      {activeIndex + 1} / {gallery.length}
                    </div>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="vehicle-thumbnails mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {gallery.map((src: string, i: number) => {
                    const isActive = i === activeIndex;
                    return (
                      <button
                        key={src + i}
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        aria-label={`View photo ${i + 1} of ${gallery.length}`}
                        className={`aspect-[3/2] overflow-hidden border transition-all ${
                          isActive
                            ? "border-foreground opacity-100"
                            : "border-border opacity-60 hover:opacity-100 hover:border-foreground/50"
                        }`}
                      >
                        <img
                          src={src}
                          alt={galleryAlt(car.name, src, i)}
                          width={imageDims(src).width}
                          height={imageDims(src).height}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              {lightboxOpen && (
                <div
                  className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
                  onClick={() => setLightboxOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(false)}
                    aria-label="Close"
                    className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  >
                    <X size={20} />
                  </button>
                  {gallery.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goPrev();
                        }}
                        aria-label="Previous photo"
                        className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goNext();
                        }}
                        aria-label="Next photo"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] bg-white/10 text-white border border-white/20">
                        {activeIndex + 1} / {gallery.length}
                      </div>
                    </>
                  )}
                  <img
                    src={activeImage}
                    alt={activeAlt}
                    width={imageDims(activeImage).width}
                    height={imageDims(activeImage).height}
                    onClick={(e) => e.stopPropagation()}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </motion.div>

            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="vehicle-intro lg:col-span-2"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
                {car.category}
              </p>
              <h1 className="font-display text-4xl md:text-5xl text-foreground">
                {car.shortName} Rental Vancouver
              </h1>
              <p className="mt-2 text-sm text-foreground/70">{car.name}</p>
              <p className="mt-3 text-muted-foreground italic">{car.tagline}</p>
              {car.slug === "mercedes-s-class-2022" && <p className="mt-2 text-muted-foreground">Mercedes S-Class rental, delivered.</p>}
              <div className="md:hidden mt-5 space-y-3">
                <p className="font-display text-2xl text-foreground">{`From $${car.pricePerDay} CAD/day`}</p>
                <InnerPageActions label={`Book this ${car.shortName}`} />
                <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" className="block text-sm underline underline-offset-4">{`★ ${RATING_VALUE} · ${REVIEW_COUNT} Google reviews`}</a>
                <p className="text-muted-foreground">CAD, before taxes and mandatory $60/day LDW. $2,000 refundable deposit.</p>
              </div>



              <p className="hidden md:block mt-4 text-foreground/90 leading-relaxed">{car.description}</p>

              <div className="hidden md:block mt-10 pb-6 border-b border-border">
                <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                  Rental Rates
                </div>
                <p className="mb-4 text-sm text-muted-foreground">All rates in Canadian dollars (CAD), before taxes. LDW is mandatory and additional; the deposit is refundable.</p>

                
                <dl className="hidden md:grid grid-cols-2 gap-px bg-border border border-border">
                  {[
                    { label: "24 hours", value: car.rates.h24 },
                    { label: "48 hours", value: car.rates.h48 },
                    { label: "3-6 days", value: car.rates.d36 },
                    { label: "7+ days", value: car.rates.d7 },
                  ].map((r) => (
                    <div key={r.label} className="bg-background p-5 text-center">
                      <dt className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        {r.label}
                      </dt>
                      <dd className="mt-2 font-display text-2xl text-foreground">{r.value}</dd>
                    </div>
                  ))}
                </dl>

                <dl className="hidden md:block mt-4 divide-y divide-border border-y border-border">
                  {[
                    { label: "Included", value: car.rates.kmIncluded },
                    { label: "Extra km", value: car.rates.extraKm },
                    { label: "Security deposit", value: car.rates.security },
                    { label: "LDW", value: car.rates.ldw },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center justify-between py-3 text-sm">
                      <dt className="text-muted-foreground">{r.label}</dt>
                      <dd className="text-foreground">{r.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                  Free delivery within Vancouver and to YVR
                </p>
              </div>

              <a
                href="/book"
                className="hidden md:block mt-8 text-center text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Book Now
              </a>
            </motion.div>
            <div className="md:hidden w-full"><MobileRateCard car={car} />
              <p className="mt-5 text-muted-foreground">{car.description}</p>
            </div>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">{car.slug === "cadillac-escalade-2023" ? "Escalade rental in Vancouver" : car.slug === "mercedes-s-class-2022" ? "Mercedes rental in Vancouver" : `${car.shortName} rental in Vancouver`}, part of our <Link to="/luxury-car-rental-vancouver" className="underline">luxury car rental in Vancouver</Link> fleet.
            {["bmw-m3-2023", "bmw-m440-2021", "porsche-macan-2023"].includes(car.slug) && <> Explore <Link to="/exotic-car-rental-vancouver" className="underline">exotic car rental in Vancouver</Link>.</>}
          </p>
        </div>
      </section>

      {/* Long-form crawlable content */}
      <section className="py-20 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 space-y-16">
          <Reveal>
            <div>
              <Eyebrow>About this vehicle</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
                About the {car.shortName}
              </h2>
              <div className="mb-6"><VehicleSpecifications car={car} /></div>
              <div className="space-y-5 text-foreground/85 leading-relaxed">
                <p>{car.description}</p>
                {carCopy[car.slug] && (
                  <>
                    <p>{carCopy[car.slug].drive}</p>
                    <p>
                      <strong className="text-foreground font-medium">Who typically rents it:</strong>{" "}
                      {carCopy[car.slug].renters}
                    </p>
                    <p>
                      <strong className="text-foreground font-medium">What's included:</strong>{" "}
                      {carCopy[car.slug].included}
                    </p>
                  </>
                )}
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div>
              <Eyebrow>Delivery</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-6">
                Where we deliver the {car.shortName}
              </h2>
              <div className="space-y-5 text-foreground/85 leading-relaxed">
                <p>
                  We deliver the {car.shortName} across Metro Vancouver (downtown, the West End,
                  Yaletown, Coal Harbour, Kitsilano, and the North Shore) with complimentary
                  delivery inside city limits. Airport pickups and drop-offs at{" "}
                  <strong className="text-foreground font-medium">YVR</strong> are available on
                  request, along with hotel and residence delivery in Richmond, Burnaby, Surrey,
                  Coquitlam, and Langley.
                </p>
                <p>
                  For clients heading further out we also handle deliveries to{" "}
                  <strong className="text-foreground font-medium">Abbotsford</strong>,{" "}
                  <strong className="text-foreground font-medium">Whistler</strong>, and the
                  Sea-to-Sky corridor. See our{" "}
                  <Link to="/delivery" className="underline underline-offset-4 hover:text-foreground">
                    full delivery details and areas
                  </Link>{" "}
                  for pricing, or{" "}
                  <Link to="/book" className="underline underline-offset-4 hover:text-foreground">
                    book the {car.shortName} online
                  </Link>{" "}
                  and note your delivery address at checkout.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {carFaqs[car.slug] && (
        <section className="py-20 border-t border-border">
          <div className="mx-auto max-w-4xl px-6 lg:px-10">
            <Reveal>
              <Eyebrow>FAQ</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-8">
                Frequently asked questions about the {car.shortName}
              </h2>
            </Reveal>
            <div className="md:hidden"><FaqList items={carFaqs[car.slug].map((f) => ({q:f.question,a:f.answer}))} prefix="vehicle" /></div>
            <dl className="hidden md:block divide-y divide-border border-y border-border">
              {carFaqs[car.slug].map((f) => (
                <div key={f.question} className="py-6">
                  <dt className="font-display text-lg md:text-xl text-foreground">
                    {f.question}
                  </dt>
                  <dd className="mt-3 text-foreground/85 leading-relaxed">{f.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}



      <section className="py-24 border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>


            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-10">
              You may also like
            </h2>
            <p className="text-sm text-muted-foreground max-w-2xl -mt-6 mb-10">
              Three more vehicles our Vancouver clients pair with the {car.shortName}.
            </p>
          </Reveal>
          <div className="related-cars grid gap-8 md:grid-cols-3">
            {others.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.1}>
                <Link to="/fleet/$slug" params={{ slug: c.slug }} className="block group">
                  <div className="aspect-[4/3] overflow-hidden bg-card border border-border">
                    <img
                      src={c.image}
                      alt={`${c.name} rental Vancouver, front view`}
                      width={imageDims(c.image).width}
                      height={imageDims(c.image).height}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-4 font-display text-xl text-foreground">{c.shortName} rental Vancouver</h3>
                  <p className="text-xs text-muted-foreground mt-1">{c.name} · {c.category}</p>
                  <p className="md:hidden mt-2 text-foreground">{`From $${c.pricePerDay}/day`}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsHighlight eyebrow={`${car.shortName} Reviews`} />
    </Layout>
  );
}
